// =====================================================================================
// INVRT Radar — web console — sign in
//
// One field. Radar issues no passwords, so there is no password input anywhere in this
// console and nothing to reset.
// =====================================================================================

import { useState, type FormEvent } from "react";
import { MailCheck } from "lucide-react";
import { useSession } from "@/auth/SessionProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";

export function SignInPage() {
  const { sendMagicLink } = useSession();
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSending(true);
    try {
      await sendMagicLink(email.trim());
      setSent(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The sign-in link could not be sent.");
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-ink px-4 py-12">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <p className="text-2xl font-semibold tracking-tight text-white">
            INVRT <span className="text-accent">Radar</span>
          </p>
          <p className="mt-2 text-sm text-ink-faint">
            Find the people worth contacting. Name the companies already on your site.
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{sent ? "Check your email" : "Sign in"}</CardTitle>
          </CardHeader>
          <CardContent>
            {sent ? (
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3 rounded-lg bg-accent-50 px-4 py-3">
                  <MailCheck className="mt-0.5 h-5 w-5 shrink-0 text-accent" aria-hidden="true" />
                  <p className="text-sm text-ink">
                    A sign-in link is on its way to{" "}
                    <span className="font-medium">{email}</span>. Open it on this device and
                    you are in — there is no password to remember.
                  </p>
                </div>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setSent(false);
                    setError(null);
                  }}
                >
                  Use a different email address
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                <Input
                  label="Work email address"
                  type="email"
                  name="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@company.com"
                  hint="We send a one-time link. Radar has no passwords."
                  {...(error ? { error } : {})}
                />
                <Button type="submit" loading={sending} className="w-full">
                  Send me a sign-in link
                </Button>
              </form>
            )}
          </CardContent>
        </Card>

        <p className="mt-6 text-center text-xs text-ink-faint">
          Radar never charges you for a contact you already own.
        </p>
      </div>
    </div>
  );
}
